import background from '@assets/jackpot-cards/dropdown-jpc.svg'
import { useShrinkTextToFit } from '@amusnet/react-ui'
import { useConfigurationStore } from '@stores/configurationStore'
import { useJackpotStore } from '@stores/jackpotStore'
import { formatDate } from '@utils/misc'
import { formatMoney } from '@utils/money'
import { useTranslation } from '@amusnet/translations/react'
import clsx from 'clsx'
import { JackpotSuitRomanNumberMap, JackpotSuits, ViewportType } from 'game-ui-types'
import React, { useEffect, useRef, useState } from 'react'

interface JackpotCardsTooltipProps {
    suit: JackpotSuits
    isActive: boolean
}

/**
 * JackpotCardsTooltip Component
 *
 * Also known as JackpotCardsDropdown handles the information of last and recent wins by suit.
 * Shown only when isActive is true and the tooltip has data.
 */
const JackpotCardsTooltip: React.FC<JackpotCardsTooltipProps> = ({ suit, isActive }) => {
    const { t } = useTranslation()
    const currency = useConfigurationStore((state) => state.currency)
    const viewport = useConfigurationStore((state) => state.viewport)
    const currencyHideCents = useConfigurationStore((state) => state.currencyHideCents)

    const [shouldShow, setShouldShow] = useState(false)

    const toolTipDateSeparator = useJackpotStore((state) => state.toolTipDateSeparator)
    // Get formatted data
    const jackpotCardsStatistics = useJackpotStore((state) => state.jackpotCardsStatistics)
    const romanNumber = JackpotSuitRomanNumberMap[suit]
    const largestWinnerDate = formatDate(
        jackpotCardsStatistics[`largestWinDateLevel${romanNumber}`],
        toolTipDateSeparator
    )
    const winAmountLargestWinner = Number(jackpotCardsStatistics[`largestWinLevel${romanNumber}`])
    const lastWinnerDate = formatDate(jackpotCardsStatistics[`lastWinDateLevel${romanNumber}`], toolTipDateSeparator)
    let usernameLastWinner = jackpotCardsStatistics[`lastWinUserLevel${romanNumber}`]
    const winAmountLastWinner = Number(jackpotCardsStatistics[`lastWinLevel${romanNumber}`])
    const numberOfWinners = jackpotCardsStatistics[`winsLevel${romanNumber}`]
    const largestWinnerAmountRef = useRef<HTMLSpanElement | null>(null)
    const lastWinAmountRef = useRef<HTMLSpanElement | null>(null)
    const width = 330

    useEffect(() => {
        setShouldShow(isActive && numberOfWinners > 0)

        // If username is too long use an abbreviated version
        if (usernameLastWinner.length > 25) {
            usernameLastWinner = `${usernameLastWinner.slice(0, 25)}...`
        }
    }, [isActive, jackpotCardsStatistics])

    useShrinkTextToFit({
        refresh: [winAmountLargestWinner, shouldShow],
        designFontSize: 24,
        bounds: { width: width * 0.7 },
        textRef: largestWinnerAmountRef,
    })

    useShrinkTextToFit({
        refresh: [winAmountLastWinner, shouldShow],
        designFontSize: 24,
        bounds: { width: width * 0.7 },
        textRef: lastWinAmountRef,
    })

    return (
        <div
            id={`jackpot-tooltip-${suit.toLowerCase()}`}
            className={clsx(
                `jackpot-tooltip-${suit.toLowerCase()}`,
                `absolute h-[400px] top-full right-0 text-[20px] leading-[24px] whitespace-break-spaces pointer-events-none z-[-1] overflow-hidden`,
                {
                    'left-0':
                        (viewport === ViewportType.MOBILE_PORTRAIT &&
                            (suit === JackpotSuits.SPADES || suit === JackpotSuits.DIAMONDS)) ||
                        (viewport === ViewportType.MOBILE_LANDSCAPE && suit === JackpotSuits.SPADES),
                    'left-[50%] -translate-x-[50%]':
                        viewport === ViewportType.MOBILE_LANDSCAPE &&
                        (suit === JackpotSuits.HEARTS || suit === JackpotSuits.DIAMONDS),
                    'pointer-events-auto': shouldShow,
                }
            )}
            style={{
                width,
                transition: 'visibility .3s linear, opacity .3s',
                visibility: shouldShow ? 'visible' : 'hidden',
                opacity: shouldShow ? 1 : 0,
            }}
        >
            <div
                className="relative w-full h-[348px] overflow-hidden"
                style={{
                    transition: 'transform 0.4s cubic-bezier(0.18, 0.89, 0.32, 1.28)',
                    transform: shouldShow ? 'translateY(0)' : 'translateY(-100%)',
                }}
            >
                <img
                    src={background}
                    alt={`${suit} Tooltip Background`}
                    className="absolute inset-0 w-full h-full object-fill"
                />

                <div className="absolute w-full h-full text-white font-bold pt-[29px] pb-[24px]">
                    <div
                        id="largest-winner-date"
                        className="absolute top-[24px] w-full px-[12px] flex align-start justify-between"
                    >
                        <div className="text-[#B6B6B6]">{t('gameui.largestWinner')}</div>
                        <div>{largestWinnerDate}</div>
                    </div>
                    <div id="largest-win-level" className="absolute top-[95px] w-full px-[12px] text-center">
                        <span
                            ref={largestWinnerAmountRef}
                            className="font-black text-[24px] text-nowrap whitespace-nowrap"
                        >
                            {formatMoney(winAmountLargestWinner, currencyHideCents)}
                        </span>
                        <span className="ml-[5px] text-[#B6B6B6]">{currency}</span>
                    </div>
                    <div
                        id="number-of-winners"
                        className="absolute top-[142px] w-full px-[12px] flex align-start justify-between"
                    >
                        <div className="text-[#B6B6B6]">{t('gameui.numberOfWinners')}</div>
                        <div>{numberOfWinners}</div>
                    </div>
                    <div
                        id="last-winner-date"
                        className="absolute top-[206px] w-full px-[12px] flex align-start justify-between"
                    >
                        <div className="text-[#B6B6B6]">{t('gameui.lastWinner')}</div>
                        <div>{lastWinnerDate}</div>
                    </div>
                    <div
                        id="last-win-user"
                        className="absolute top-[276px] w-full px-[12px] text-[#B6B6B6] text-center"
                    >
                        {usernameLastWinner.length > 20
                            ? `${usernameLastWinner.substring(0, 20)}...`
                            : usernameLastWinner}
                    </div>
                    <div id="last-win-amount" className="absolute top-[311px] w-full px-[12px] text-center">
                        <span ref={lastWinAmountRef} className="font-black text-[24px] text-nowrap whitespace-nowrap">
                            {formatMoney(winAmountLastWinner, currencyHideCents)}
                        </span>
                        <span className="ml-[5px] text-[#B6B6B6]">{currency}</span>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default JackpotCardsTooltip
